import type { FamilyHistoryFact, FamilyHistoryRecord, FamilyRelative } from '@liza/fhir-utils';
import type { TFunction } from 'i18next';
import {
  formatAge,
  formatDeceased,
  formatRecordConditions,
  formatRelationship,
  relativeLabel,
} from './familyMemberHistoryUtils';
import type { PromotionStatus } from './RecordListItem';

/** Selection and promotion callbacks shared by every row of the family history list. */
export type FamilyHistoryItemProps = {
  onSelectFamilyMember?: (id: string) => void;
  selectedFamilyMemberId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

export const factOnset = (fact: FamilyHistoryFact, t: TFunction<'records'>): string | undefined =>
  fact.onsetAge ? formatAge(fact.onsetAge, t) : undefined;

/** "Colon cancer at 57 years", or the bare label when no onset is recorded. */
export const conditionLine = (fact: FamilyHistoryFact, t: TFunction<'records'>): string => {
  const onset = factOnset(fact, t);
  return onset ? t('conditionAtAge', { condition: fact.condition.label, age: onset }) : fact.condition.label;
};

/** "Sister at 57 years", or the bare relative when no onset is recorded. */
export const relativeLine = (fact: FamilyHistoryFact, t: TFunction<'records'>): string => {
  const label = relativeLabel(fact.relative.slot, t);
  const onset = factOnset(fact, t);
  return onset ? t('conditionAtAge', { condition: label, age: onset }) : label;
};

export const relativeTitle = (relative: FamilyRelative, t: TFunction<'records'>): string => {
  const label = relativeLabel(relative.slot, t);
  return relative.record.name ? `${label} · ${relative.record.name}` : label;
};

/** The relationship the source recorded, with the relative's name when one was given. */
export const recordTitle = (record: FamilyHistoryRecord, t: TFunction<'records'>): string => {
  const relationship = formatRelationship(record.relationship, t);
  return record.name ? `${relationship} · ${record.name}` : relationship;
};

export const recordDescription = (record: FamilyHistoryRecord, t: TFunction<'records'>): string | undefined => {
  const ageOrDeceased = formatDeceased(record, t) ?? formatAge(record.ageAge ?? record.ageRange ?? record.ageString, t);
  return [ageOrDeceased, formatRecordConditions(record, t)].filter(Boolean).join(' · ') || undefined;
};
